import React, { FC, useEffect, useState } from 'react';

import { Button, message } from 'antd';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import i18n from '@/content';
import ShareHalf from './ShareHalf';
import VirtualUploadList from './VirtualUploadList';
import StepNavigation from './StepNavigation';
import PolicyStep from './PolicyStep';
import RulesLoadingState from './RulesLoadingState';
import hasAnyRecipientMissingDetails from '../../Common/MissingDetails/hasAnyRecipientMissingDetails';
import { useStores, useUploadCompletion } from '../../hooks';
import { checkHasPhoneVerificationInFilesList, checkHasPasswordVerificationInFilesList, checkHasPasswordProtectionInFilesList } from '../../utils';
import { applyRules, ApplyRulesResponse } from '../../../api/requests';
import {
    REQUIRED_PHONE_VERIFICATION,
    REQUIRED_PASSWORD_VERIFICATION,
    PASSWORD_PROTECTION,
} from '../../../consts';
import config from '../../../config/env';
import styles from './UploadFiles.module.scss';

const nameSpace = 'uploadFiles';

interface UploadFilesProps {
    closeDrawer: () => void;
    onShareClick: () => void;
    uploadOnly?: boolean;
    onRulesResults?: (results: ApplyRulesResponse) => void;
    onStepChange?: (step: number) => void;
}

const UploadFiles: FC<UploadFilesProps> = observer(({
    closeDrawer,
    onShareClick,
    uploadOnly = false,
    onRulesResults,
    onStepChange,
}) => {
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [isLoadingRules, setIsLoadingRules] = useState<boolean>(false);
    const [rulesResults, setRulesResults] = useState<ApplyRulesResponse>([]);
    const {
        uploadFilesStore,
        policyStore,
        sharedUsersStore,
        appStore: { authSettingsStore: { API } },
        userStore,
        usersPhonesStore,
        usersListStore,
    } = useStores();

    useUploadCompletion(uploadFilesStore);

    useEffect(() => {
        if (!policyStore.loadingPolicy) {
            policyStore.fetchPolicyList({ isSilently: true });
        }
    }, []);

    const handleNextClick = async (): Promise<void> => {
        const { successfullyUploadedItems } = uploadFilesStore;
        const { recipientsList } = sharedUsersStore;
        const senderEmail = userStore.currentUserEmail;

        // Extract file IDs
        const fileIds = successfullyUploadedItems.map((item) => item.fid);

        // Separate recipients and groups
        const recipients = recipientsList
            .filter((r) => r.type === 'user')
            .map((r) => r.email);
        const groups = recipientsList
            .filter((r) => r.type === 'group')
            .map((r) => r.id);

        // Switch to Step 2 immediately and show loading state
        setCurrentStep(1);
        onStepChange?.(1);
        setIsLoadingRules(true);

        try {
            const results = await applyRules(API, {
                file_ids: fileIds,
                sender: senderEmail,
                recipients,
                groups,
            });

            // Apply rule-assigned policies to files in the store
            const policyRules = results.filter((r) => r.action === 'policy' && r.policy_id);
            await Promise.all(
                policyRules.map((rule) => {
                    const policy = policyStore.policyList.find((p) => p.id === rule.policy_id);
                    if (policy) {
                        const file = successfullyUploadedItems.find((f) => f.fid === rule.file_id);
                        return uploadFilesStore.changePolicy({
                            newPolicy: { id: policy.id, title: policy.title, isMfa: policy.isMfa },
                            fileId: rule.file_id,
                            isFolder: file?.isFolder || false,
                            isExternalStorage: true, // suppress global spinner — RulesLoadingState handles loading UI
                        });
                    }
                    return Promise.resolve();
                }),
            );

            setRulesResults(results);
            onRulesResults?.(results);
        } catch (error) {
            console.error('Failed to apply rules:', error);
            message.error(i18n.t('general.specterxCommon.couldNotShareTryAgain'));
            // Go back to Step 1 on error
            setCurrentStep(0);
            onStepChange?.(0);
        } finally {
            setIsLoadingRules(false);
        }
    };

    const goToPreviousStep = (): void => {
        setCurrentStep(0);
        onStepChange?.(0);
    };

    const {
        isUploading,
        atLeastOneUploadedItem,
        successfullyUploadedItems,
    } = uploadFilesStore;
    const { hasRecipients } = sharedUsersStore;
    const { policyList } = policyStore;

    const requiresPhoneVerification = checkHasPhoneVerificationInFilesList(
        successfullyUploadedItems,
        policyList,
    );

    const requiresPasswordVerification = checkHasPasswordVerificationInFilesList(
        successfullyUploadedItems,
        policyList,
    );

    const requiresPasswordProtection = checkHasPasswordProtectionInFilesList(
        successfullyUploadedItems,
        policyList,
    );

    // Check if any rule-assigned policies also require phone/password
    const assignedPolicyRequiresPhone = rulesResults
        .filter((r) => r.action === 'policy' && r.policy_id)
        .some((r) => {
            const policy = policyList.find((p) => p.id === r.policy_id);
            return policy?.settings?.access?.some(
                (s) => s.id === REQUIRED_PHONE_VERIFICATION && s.value,
            );
        });

    const assignedPolicyRequiresPassword = rulesResults
        .filter((r) => r.action === 'policy' && r.policy_id)
        .some((r) => {
            const policy = policyList.find((p) => p.id === r.policy_id);
            return policy?.settings?.access?.some(
                (s) => (s.id === REQUIRED_PASSWORD_VERIFICATION || s.id === PASSWORD_PROTECTION) && s.value,
            );
        });

    const needsPhone = requiresPhoneVerification || assignedPolicyRequiresPhone;
    const needsPassword = requiresPasswordVerification || requiresPasswordProtection || assignedPolicyRequiresPassword;

    const hasMissingDetailsUnfilled = hasAnyRecipientMissingDetails({
        needsPhone,
        needsPassword,
        sharedUsersStore,
        usersPhonesStore,
        usersListStore,
    });

    const isContinueDisabled = !atLeastOneUploadedItem
        || !hasRecipients
        || isUploading;
    // Check if all files are blocked for all recipients
    const allFilesBlockedForAll = (() => {
        if (!rulesResults.length) return false;
        const { successfullyUploadedItems } = uploadFilesStore;
        if (!successfullyUploadedItems.length) return false;

        const blockRules = rulesResults.filter((r) => r.action === 'block');
        const userRecipientEmails = sharedUsersStore.recipientsList
            .filter((r) => r.type === 'user')
            .map((r) => r.email);

        if (!userRecipientEmails.length) return false;

        return successfullyUploadedItems.every((file) => {
            const blockedForFile = new Set<string>();
            blockRules.forEach((rule) => {
                if (rule.file_id === file.fid) {
                    rule.recipients?.forEach((email) => blockedForFile.add(email));
                }
            });
            return userRecipientEmails.every((email) => blockedForFile.has(email));
        });
    })();

    const isShareDisabled = hasMissingDetailsUnfilled || allFilesBlockedForAll;
    const sharedItemType = uploadFilesStore.currentlyUploadingFolders.length ? 'folder' : 'file';

    const isStep1 = currentStep === 0;
    const isStep2 = currentStep === 1;

    return (
        <div className={styles['upload-drawer-content']}>
            {!uploadOnly && (
                <StepNavigation
                    currentStep={currentStep}
                />
            )}
            <div className={styles['main-content']}>
                {isStep1 && (
                    <>
                        <VirtualUploadList uploadOnly={uploadOnly} />
                        {!uploadOnly && (
                            <ShareHalf
                                sharedItemType={sharedItemType}
                                requiresPhoneVerification={false}
                                requiresPasswordVerification={false}
                                requiresPasswordProtection={false}
                                hasRecipients={hasRecipients}
                            />
                        )}
                    </>
                )}
                {isStep2 && !uploadOnly && (
                    isLoadingRules ? (
                        <RulesLoadingState />
                    ) : (
                        <PolicyStep
                            rulesResults={rulesResults}
                            requiresPhoneVerification={requiresPhoneVerification}
                            requiresPasswordVerification={requiresPasswordVerification || requiresPasswordProtection}
                        />
                    )
                )}
            </div>
            <div className={classNames(
                styles['buttons-container'],
                {
                    [styles['single-child']]: uploadOnly,
                },
            )}
            >
                {/* Step 1: Cancel and Next buttons */}
                {isStep1 && (
                    <>
                        <Button
                            disabled={isUploading}
                            onClick={closeDrawer}
                            type="link"
                            className={styles['cancel-button']}
                            data-testid="uploadDrawer_closeWithoutSharing"
                        >
                            {uploadOnly ? i18n.t('general.buttons.done') : i18n.t(`${nameSpace}.buttons.close`)}
                        </Button>
                        {!uploadOnly && (
                            <Button
                                disabled={isContinueDisabled}
                                type="primary"
                                onClick={handleNextClick}
                                className={styles['done-button']}
                                data-testid="uploadDrawer_nextButton"
                            >
                                {i18n.t('general.buttons.next')}
                            </Button>
                        )}
                    </>
                )}
                {/* Step 2: Back and Share buttons */}
                {isStep2 && !uploadOnly && (
                    <>
                        <Button
                            disabled={isLoadingRules}
                            onClick={goToPreviousStep}
                            type="link"
                            className={styles['cancel-button']}
                            data-testid="uploadDrawer_backButton"
                        >
                            {i18n.t('general.buttons.back')}
                        </Button>
                        <Button
                            type="primary"
                            disabled={isShareDisabled || isLoadingRules}
                            onClick={onShareClick}
                            className={styles['done-button']}
                            data-testid="uploadDrawer_shareButton"
                        >
                            {i18n.t('general.buttons.share')}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
});

export default UploadFiles;
